import React from 'react';
import uuid from 'uuid/v4';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Dispatch } from 'hellospa/redux/types';
import HelloModal from 'common/components/hello-modal';
import { externalFolderContents } from 'hellospa/page/prep-and-send/data/actions/integration';
import {
  ExternalFileContents, UploadIntegrationType, ExternalFolderDataResponse,
} from 'hellospa/page/prep-and-send/data/types/integration';
import styles from './styles.scss';
import backIcon from './assets/back-icon.svg';
import DocumentIcon from 'hellospa/icons/document-icon';


type Props = {
  // only Box & Evernote uses a custom picker
  integration: UploadIntegrationType.Box | UploadIntegrationType.Evernote,
  isOpen: boolean,
  integrationIcon: string,
  onFileSelect: (file: ExternalFileContents) => () => void,
  onClose: () => void,
}

type State = {
  requestGuid: string | null,
  files: ExternalFileContents[],
  folders: ExternalFileContents[],
  numResults: number,
  pageSize: number,
  offset: number,
  folderReference: string,
  parentFolders: string[],
  loading: boolean,
  error: boolean,
}

type LocalState = {
  offset: number,
  folderReference: string,
  showMore: boolean,
}

export default function CustomPicker({
  integration,
  isOpen,
  integrationIcon,
  onClose,
  onFileSelect,
}: Props) {
  const dispatch = useDispatch<Dispatch>();

  // useEffect uses local state to make calls
  const [localState, setLocalState] = React.useState<LocalState>({
    offset: 0,
    folderReference: '0',
    showMore: false,
  });

  // setting default initial values
  const [state, setState] = React.useState<State>({
    requestGuid: null,
    files: [],
    folders: [],
    numResults: 0,
    pageSize: 100,
    offset: 0,
    folderReference: '0', // this is the root folder
    parentFolders: [],
    loading: false,
    error: false,
  });

  const fetchExternalFolderContents = React.useCallback(async (
    offset: number,
    folderReference: string,
    shouldShowMore: boolean = false,
  ) => {
    const requestGuid = uuid();

    setState((state) => {
      // parentFolders acts as a stack that adds the folder clicked to the top,
      // so it can be used to go back to the parent folder when available
      let parentFolders = state.parentFolders;
      // if the first item in the stack is the requested folder and is not the root ('0')
      // remove the first element since we are moving back to the parent folder
      if (parentFolders[0] === folderReference && parentFolders[0] !== '0') {
        parentFolders.shift();
      } else if (!shouldShowMore && !parentFolders.includes(state.folderReference)) {
        // if we're not asking for more results for the same folder and the folder
        // isn't included, then add the current folder as the first element
        // since we're going into a child folder
        parentFolders.unshift(state.folderReference);
      }
      // if the target folder is the parent, clear out the stack
      if (folderReference === '0') {
        parentFolders = [];
      }

      return {
        ...state,
        requestGuid,
        offset,
        folderReference,
        parentFolders,
        loading: true,
      };
    });

    try {
      // retrieve the integration contents here
      // asserting the type since if it reaches here, it was a successful response
      const data = await dispatch(
        externalFolderContents(integration, offset, folderReference),
      ) as ExternalFolderDataResponse;

      setState((state: State) => {
        if (state.requestGuid === requestGuid) {
          return {
            ...state,
            // when the "Show more..." link is clicked we will just append with existing data
            // since we are retrieving more files from the same "folder" because page size.
            // Otherwise if it's a different folder, just overwrite with new content.
            files: shouldShowMore ? state.files.concat(data.files) : data.files,
            folders: shouldShowMore ? state.folders.concat(data.folders) : data.folders,

            pageSize: data.pageSize,
            // offset is passed to the endpoint to retrieve the next set of contents
            offset: state.offset + data.pageSize,
            numResults: data.folders.length + data.files.length,
            // to show a "processing" text
            loading: false,
          };
        }
        return { ...state, loading: false };
      });
    } catch (e) {
      setState((state: State) => ({ ...state, error: true, loading: false }));
    }

  }, [dispatch, integration]);

  React.useEffect(() => {
    // makes request based on whether the local state changed or not
    fetchExternalFolderContents(localState.offset, localState.folderReference, localState.showMore);
  }, [fetchExternalFolderContents, localState]);

  // updates local state to trigger the request
  // because useEffect above depends on it
  const onItemClick = React.useCallback((
    offset: number,
    folderReference: string,
    showMore: boolean = false,
  ) => {
    setLocalState((localState) => {
      return {
        ...localState,
        offset,
        folderReference,
        showMore,
      };
    });
  }, []);

  const folders = state.folders.length > 0
    ? state.folders.map((folder) => (
      <li
        className={styles.externalContent}
        data-qa-ref="custom-picker-content-folder"
        key={folder.id}
        // offset is set to 0 when a folder is clicked to not skip any files
        onClick={() => onItemClick(0, folder.id)}
      >
        {folder.name}
      </li>
    ))
    : null;

  const files = state.files.length > 0
    ? state.files.map((file) => (
      <li
        className={styles.externalContent}
        data-qa-ref="custom-picker-content-file"
        key={file.id}
        // trigger a select file here so the parent can try to retrieve the file
        onClick={onFileSelect(file)}
      >
        <span className={styles.contentIcon}>
          <DocumentIcon />
        </span>
        {file.name}
      </li>
    ))
    : null;

  // parent folder will show up if we go deeper into the folder
  const parentFolder = localState.folderReference !== '0' && state.parentFolders.length > 0
    ? (
      <li
        className={styles.externalContent}
        key={state.parentFolders[0]}
        data-qa-ref="custom-picker-parent-folder"
        onClick={() => onItemClick(0, state.parentFolders[0])}
      >
        <span className={styles.contentIcon}><img src={backIcon} /></span>
        <FormattedMessage
          id=""
          description="link to go back to the parent folder"
          defaultMessage="Parent folder" />
      </li>
    ) : null;

  const showMoreLink = state.numResults > 0 && state.numResults >= state.pageSize
    ? (
      <a
        key="show-more-link" // adding key because react complains about needing unique keys
        className={styles.showMoreLink}
        data-qa-ref="custom-picker-show-more-link"
        onClick={() => onItemClick(state.offset, state.folderReference, true)}
      >
        <FormattedMessage
          id=""
          description="anchor text to show more contents"
          defaultMessage="Show more..." />
      </a>
    ) : null;

  // combine into contents for displaying in the modal
  const contents = [parentFolder, folders, files, showMoreLink];
  const loadingText = <li className={styles.loadingText}>
    <FormattedMessage
      id=""
      description="loading text when contents are being retrieved"
      defaultMessage="Loading files..." />
  </li>;

  // if no results found & is not a result of clicking on "show more..."
  // & is not fetching currently with no errors, show no results text
  const emptyResults = state.numResults === 0
    && !localState.showMore
    && !state.loading
    && !state.error
    && <li className={styles.emptyResults}>
      <FormattedMessage
        id=""
        description="text when no contents retrieved"
        defaultMessage="No files found" />
    </li>;

  const errorText = state.error
    ? <li className={styles.error}>
      <FormattedMessage
        id=""
        description="error message when integration contents cannot be retrieved successfully"
        defaultMessage="Sorry something went wrong, please try again later." />
    </li>
    : null;

  return (
    <HelloModal
      isOpen={isOpen}
      contentLabel={
        <img src={integrationIcon} />
      }
      width="700px"
      onRequestClose={onClose}
    >
      <div className={styles.externalFileContainer}>
        <ul className={styles.contentList}>
          {state.loading && !state.error ? loadingText : contents}
          {emptyResults}
          {errorText}
        </ul>
      </div>
    </HelloModal>
  );

}
